/**
 * /admin/publicos — Atualização de públicos (Meta Ads / Google Ads).
 *
 * Baixa as listas de leads por captura (nome, e-mail, telefone E.164) no
 * período escolhido. O nome do arquivo é o nome da LP de captura.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, Users } from "lucide-react";
import { zipSync, strToU8 } from "fflate";

import { AdminGate } from "@/components/admin/AdminGate";
import { PeriodFilter, usePeriodFilter } from "@/components/admin/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { neutralizeCsvValue } from "@/lib/csv-safe";
import { AUDIENCE_GROUP_LABELS, type AudienceGroup } from "@/lib/leads/audiences";
import {
  getAudienceRows,
  getAudienceRowsBatch,
  getAudienceSummary,
  type AudienceRow,
  type AudienceSummary,
} from "@/lib/leads/audiences.functions";

export const Route = createFileRoute("/admin/publicos")({
  head: () => ({
    meta: [
      { title: "Atualização de Públicos — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: () => (
    <AdminGate route="/admin/publicos">
      <PublicosPage />
    </AdminGate>
  ),
});

const GROUP_ORDER: AudienceGroup[] = ["COMPLETAS", "BLACK", "AULAS", "EMENTAS", "FUNIS", "PRE-CHECKOUT", "OUTROS"];

const CSV_COLUMNS = [
  "nome",
  "email",
  "telefone",
  "captura",
  "data",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_completa",
] as const;

function csvEscape(raw: string) {
  const v = neutralizeCsvValue(raw ?? "");
  return /[",;\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

function buildCsv(rows: AudienceRow[]) {
  const body = rows.map((r) => CSV_COLUMNS.map((c) => csvEscape(String(r[c] ?? ""))).join(";"));
  return "\uFEFF" + [CSV_COLUMNS.join(";"), ...body].join("\n");
}

type ReadyFile = { name: string; blob: Blob; count: number };

function triggerDownload(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revogar na hora cancela o download em vários navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

async function deliverFile(blob: Blob, name: string) {
  const file = new File([blob], name, { type: blob.type });
  const touch = matchMedia("(pointer: coarse)").matches;
  if (touch && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: name });
      return;
    } catch (e) {
      if ((e as DOMException).name === "AbortError") return;
    }
  }
  triggerDownload(name, blob);
}

function csvBlob(rows: AudienceRow[]) {
  return new Blob([buildCsv(rows)], { type: "text/csv;charset=utf-8" });
}

function zipBlob(files: { file: string; rows: AudienceRow[] }[]) {
  const entries: Record<string, Uint8Array> = {};
  for (const f of files) entries[`${f.file}.csv`] = strToU8(buildCsv(f.rows));
  const zipped = zipSync(entries, { level: 6 });
  const stamp = new Date().toISOString().slice(0, 10);
  return {
    name: `publicos-${stamp}.zip`,
    blob: new Blob([zipped as unknown as BlobPart], { type: "application/zip" }),
  };
}

function PublicosPage() {
  const period = usePeriodFilter("all");
  const [includePartial, setIncludePartial] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState<Record<string, ReadyFile>>({});

  const summaryFn = useServerFn(getAudienceSummary);
  const rowsFn = useServerFn(getAudienceRows);
  const rowsBatchFn = useServerFn(getAudienceRowsBatch);

  const summary = useQuery({
    queryKey: ["audiences", period.range.from, period.range.to, includePartial],
    queryFn: () =>
      summaryFn({
        data: { fromIso: period.range.from, toIso: period.range.to, includePartial },
      }),
    retry: false,
    staleTime: 60_000,
  });

  const grouped = useMemo(() => {
    const lists = summary.data?.lists ?? [];
    return GROUP_ORDER.map((g) => ({
      group: g,
      lists: lists.filter((l) => l.group === g),
    })).filter((g) => g.lists.length > 0);
  }, [summary.data]);

  const setReadyKey = (key: string, f: ReadyFile) => setReady((p) => ({ ...p, [key]: f }));
  const saveReady = (key: string) => {
    const f = ready[key];
    if (!f) return;
    void deliverFile(f.blob, f.name);
    setReady((p) => {
      const n = { ...p };
      delete n[key];
      return n;
    });
  };

  const buildFromBatch = (res: { file: string; rows: AudienceRow[] }[]): ReadyFile | null => {
    const withRows = res.filter((r) => r.rows.length > 0);
    if (!withRows.length) return null;
    if (withRows.length === 1) {
      const only = withRows[0]!;
      return { name: `${only.file}.csv`, blob: csvBlob(only.rows), count: only.rows.length };
    }
    return { ...zipBlob(withRows), count: withRows.reduce((s, r) => s + r.rows.length, 0) };
  };

  const handleDownload = async (list: AudienceSummary) => {
    setBusy(list.key);
    try {
      const res = await rowsFn({
        data: {
          key: list.key,
          fromIso: period.range.from,
          toIso: period.range.to,
          includePartial,
        },
      });
      if (!res.rows.length) {
        toast.error("Nenhum lead nesse período para essa lista.");
        return;
      }
      setReadyKey(list.key, { name: `${res.file}.csv`, blob: csvBlob(res.rows), count: res.rows.length });
      toast.success(`Lista pronta: ${res.file}.csv · ${res.rows.length} contatos. Clique em "Baixar".`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao gerar a lista");
    } finally {
      setBusy(null);
    }
  };

  const prepareBatch = async (slot: "__all" | "__selected", keys: string[]) => {
    if (!keys.length) {
      toast.error("Selecione pelo menos uma lista com leads no período.");
      return;
    }
    setBusy(slot);
    try {
      const res = await rowsBatchFn({
        data: { keys, fromIso: period.range.from, toIso: period.range.to, includePartial },
      });
      const f = buildFromBatch(res);
      if (!f) {
        toast.error("Nenhum lead nesse período para essas listas.");
        return;
      }
      setReadyKey(slot, f);
      toast.success(`Pronto: ${f.name} · ${f.count.toLocaleString("pt-BR")} contatos. Clique em "Baixar".`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao gerar as listas");
    } finally {
      setBusy(null);
    }
  };

  const handleDownloadAll = () =>
    prepareBatch(
      "__all",
      (summary.data?.lists ?? []).filter((l) => l.leads > 0).map((l) => l.key),
    );

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleGroup = (keys: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  const selectedKeys = useMemo(
    () => (summary.data?.lists ?? []).filter((l) => selected.has(l.key) && l.leads > 0).map((l) => l.key),
    [summary.data, selected],
  );

  const handleDownloadSelected = () => prepareBatch("__selected", selectedKeys);

  return (
    <div className="min-h-screen px-3 py-8 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-admin-accent">
              <Users className="h-5 w-5" />
              <span className="text-xs font-bold uppercase tracking-[0.2em]">Atualização de públicos</span>
            </div>
            <h1 className="mt-2 text-2xl font-bold text-white">Listas para Meta Ads e Google Ads</h1>
            <p className="mt-1 max-w-2xl text-sm text-white/60">
              Baixe as listas de cada captura para os públicos do Meta Ads e Google Ads.
            </p>
          </div>
          <Link
            to="/admin"
            className="inline-flex items-center rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Painel
          </Link>
        </div>

        <div className="mb-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <PeriodFilter
            preset={period.preset}
            setPreset={period.setPreset}
            customFrom={period.customFrom}
            setCustomFrom={period.setCustomFrom}
            customTo={period.customTo}
            setCustomTo={period.setCustomTo}
          />
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-white/70">
              <Switch checked={includePartial} onCheckedChange={setIncludePartial} />
              Incluir leads parciais (abandono de formulário)
            </label>
            {ready.__selected ? (
              <Button
                size="sm"
                onClick={() => saveReady("__selected")}
                className="bg-emerald-500 text-[#13123a] hover:bg-emerald-400"
              >
                <Download className="mr-2 h-4 w-4" />
                Baixar {ready.__selected.name} ({ready.__selected.count.toLocaleString("pt-BR")} contatos)
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleDownloadSelected}
                disabled={!!busy || summary.isLoading || !selectedKeys.length}
                className="bg-[#f5b022] text-[#13123a] hover:bg-[#e09f17]"
              >
                <Download className="mr-2 h-4 w-4" />
                {busy === "__selected"
                  ? "Gerando…"
                  : `Preparar listas selecionadas${selectedKeys.length ? ` (${selectedKeys.length})` : ""}`}
              </Button>
            )}
            {ready.__all ? (
              <Button
                size="sm"
                onClick={() => saveReady("__all")}
                className="bg-emerald-500 text-[#13123a] hover:bg-emerald-400"
              >
                <Download className="mr-2 h-4 w-4" />
                Baixar {ready.__all.name} ({ready.__all.count.toLocaleString("pt-BR")} contatos)
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDownloadAll}
                disabled={!!busy || summary.isLoading}
                className="border-white/10 bg-white/5 text-white hover:bg-white/10"
              >
                <Download className="mr-2 h-4 w-4" />
                {busy === "__all" ? "Gerando…" : "Preparar todas as listas"}
              </Button>
            )}
            {selected.size > 0 && (
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                className="text-xs text-white/50 underline hover:text-white/80"
              >
                Limpar seleção
              </button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => summary.refetch()}
              className="border-white/10 bg-white/5 text-white hover:bg-white/10"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Recarregar
            </Button>
            {summary.data && (
              <span className="text-xs text-white/50">
                {summary.data.totalLeads.toLocaleString("pt-BR")} pessoas únicas no período
              </span>
            )}
          </div>
        </div>

        {summary.isLoading && <div className="text-sm text-white/60">Carregando listas…</div>}
        {summary.error && (
          <div className="rounded-xl border border-red-400/30 bg-red-400/10 p-4 text-sm text-red-200">
            {summary.error instanceof Error ? summary.error.message : "Erro ao carregar"}
          </div>
        )}

        <div className="space-y-8">
          {grouped.map((g) => (
            <section key={g.group}>
              <div className="mb-3 flex items-center gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-admin-accent">
                  {AUDIENCE_GROUP_LABELS[g.group]}
                </span>
                <span className="h-px flex-1 bg-gradient-to-r from-[#f5b022]/40 to-transparent" />
              </div>
              <div className="overflow-hidden rounded-2xl border border-white/10">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.04] text-[11px] uppercase tracking-wide text-white/50">
                    <tr>
                      <th className="w-10 px-4 py-2.5 text-left font-semibold">
                        <Checkbox
                          aria-label={`Selecionar tudo em ${AUDIENCE_GROUP_LABELS[g.group]}`}
                          checked={
                            g.lists.some((l) => l.leads > 0) &&
                            g.lists.filter((l) => l.leads > 0).every((l) => selected.has(l.key))
                          }
                          onCheckedChange={(v) =>
                            toggleGroup(
                              g.lists.filter((l) => l.leads > 0).map((l) => l.key),
                              v === true,
                            )
                          }
                          className="border-white/30 data-[state=checked]:border-[#f5b022] data-[state=checked]:bg-[#f5b022] data-[state=checked]:text-[#13123a]"
                        />
                      </th>
                      <th className="px-4 py-2.5 text-left font-semibold">Captura / LP</th>
                      <th className="px-4 py-2.5 text-left font-semibold">Arquivo</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Leads</th>
                      <th className="px-4 py-2.5 text-right font-semibold">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.lists.map((l) => (
                      <tr key={l.key} className="border-t border-white/5">
                        <td className="px-4 py-3">
                          <Checkbox
                            aria-label={`Selecionar ${l.label}`}
                            disabled={!l.leads}
                            checked={selected.has(l.key)}
                            onCheckedChange={() => toggle(l.key)}
                            className="border-white/30 data-[state=checked]:border-[#f5b022] data-[state=checked]:bg-[#f5b022] data-[state=checked]:text-[#13123a]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-white">{l.label}</div>
                          {l.origens.length > 0 && (
                            <div className="mt-0.5 text-[11px] text-white/40">{l.origens.join(" · ")}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-white/60">{l.file}.csv</td>

                        <td className="px-4 py-3 text-right font-semibold text-white">
                          {l.leads.toLocaleString("pt-BR")}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {ready[l.key] ? (
                            <Button
                              size="sm"
                              onClick={() => saveReady(l.key)}
                              className="bg-[#f5b022] text-[#13123a] hover:bg-[#e09f17]"
                            >
                              <Download className="mr-2 h-4 w-4" />
                              Baixar ({ready[l.key]!.count.toLocaleString("pt-BR")})
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!l.leads || busy === l.key}
                              onClick={() => handleDownload(l)}
                              className="border-white/10 bg-white/5 text-white hover:bg-white/10"
                            >
                              <Download className="mr-2 h-4 w-4" />
                              {busy === l.key ? "Gerando…" : "Preparar"}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
